/**
 * Markdown — a small, dependency-free renderer for the assistant's replies.
 *
 * The chat model emits standard markdown (headings, bold/italic, lists, inline
 * and fenced code, links, blockquotes, GFM tables). This block parser covers
 * those cases and renders them with the app's inline-style aesthetic + theme
 * variables. It is intentionally not a full CommonMark implementation — nested
 * lists and inline nesting are flattened, which is fine for chat output.
 */
import React from "react";

// Theme accents (Heroku-purple palette).
const PURPLE = "#79589F";
const PURPLE_DARK = "#5B3D86";
const PURPLE_TINT = "rgba(121, 88, 159, 0.08)";
const PURPLE_TINT_STRONG = "rgba(121, 88, 159, 0.16)";

// ── inline: code spans, bold, italic, links ──────────────────
const INLINE_RE =
  /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|\[([^\]]+)\]\(([^)\s]+)\))/g;

const codeStyle = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.85em",
  color: PURPLE_DARK,
  backgroundColor: PURPLE_TINT,
  border: `1px solid ${PURPLE_TINT_STRONG}`,
  borderRadius: "4px",
  padding: "0.05em 0.3em",
};

function emphasis(text, kp) {
  const out = [];
  let last = 0;
  let m;
  let n = 0;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${kp}-${n++}`;
    if (m[2] || m[3]) out.push(<strong key={key} style={{ color: PURPLE_DARK }}>{m[2] || m[3]}</strong>);
    else if (m[4] || m[5]) out.push(<em key={key}>{m[4] || m[5]}</em>);
    else if (m[6])
      out.push(
        <a key={key} href={m[7]} target="_blank" rel="noreferrer" style={{ color: "#79589F" }}>
          {m[6]}
        </a>
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function inline(text, kp) {
  // Split out inline `code` first so emphasis isn't applied inside it.
  const out = [];
  text.split(/(`[^`]+`)/g).forEach((seg, i) => {
    if (/^`[^`]+`$/.test(seg)) {
      out.push(
        <code key={`${kp}-c${i}`} style={codeStyle}>
          {seg.slice(1, -1)}
        </code>
      );
    } else if (seg) {
      out.push(...emphasis(seg, `${kp}-e${i}`));
    }
  });
  return out;
}

// ── block parsing ────────────────────────────────────────────
const splitRow = (line) =>
  line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

const isSeparator = (line) =>
  line && line.includes("-") && /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes("|");

const startsBlock = (l) =>
  /^\s*```/.test(l) ||
  /^#{1,6}\s/.test(l) ||
  /^>\s?/.test(l) ||
  /^\s*[-*+]\s+/.test(l) ||
  /^\s*\d+\.\s+/.test(l) ||
  /^(-{3,}|\*{3,}|_{3,})\s*$/.test(l.trim());

function parse(md) {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    // fenced code
    if (/^\s*```/.test(line)) {
      const buf = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      blocks.push({ type: "code", text: buf.join("\n") });
      continue;
    }
    // heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ type: "heading", level: h[1].length, text: h[2] }); i++; continue; }
    // hr
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) { blocks.push({ type: "hr" }); i++; continue; }
    // table (header row + separator row)
    if (line.includes("|") && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) { rows.push(splitRow(lines[i])); i++; }
      blocks.push({ type: "table", header, rows });
      continue;
    }
    // blockquote
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, "")); i++; }
      blocks.push({ type: "quote", text: buf.join("\n") });
      continue;
    }
    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*+]\s+/, "")); i++; }
      blocks.push({ type: "ul", items });
      continue;
    }
    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      blocks.push({ type: "ol", items });
      continue;
    }
    // paragraph
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i]) && !isSeparator(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: buf.join(" ") });
  }
  return blocks;
}

const H_SIZE = { 1: "1.05rem", 2: "0.95rem", 3: "0.88rem", 4: "0.84rem", 5: "0.82rem", 6: "0.82rem" };

// ── render ───────────────────────────────────────────────────
const Markdown = ({ text }) => {
  const blocks = parse(text || "");
  return (
    <div style={{ fontSize: "0.82rem", lineHeight: 1.55, color: "var(--ev-text)" }}>
      {blocks.map((b, i) => {
        const k = `b${i}`;
        switch (b.type) {
          case "heading":
            return (
              <div
                key={k}
                style={{
                  fontWeight: 700,
                  fontSize: H_SIZE[b.level],
                  margin: i ? "0.7rem 0 0.3rem" : "0 0 0.3rem",
                  color: b.level <= 2 ? PURPLE : PURPLE_DARK,
                  borderBottom: b.level === 1 ? `1px solid ${PURPLE_TINT_STRONG}` : "none",
                  paddingBottom: b.level === 1 ? "0.2rem" : 0,
                }}
              >
                {inline(b.text, k)}
              </div>
            );
          case "hr":
            return <hr key={k} style={{ border: "none", borderTop: "1px solid var(--ev-border)", margin: "0.6rem 0" }} />;
          case "code":
            return (
              <pre key={k} style={{ backgroundColor: "var(--ev-surface)", border: "1px solid var(--ev-border)", borderRadius: "6px", padding: "0.6rem 0.7rem", overflowX: "auto", fontSize: "0.75rem", margin: "0.4rem 0", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                {b.text}
              </pre>
            );
          case "quote":
            return (
              <div key={k} style={{ borderLeft: `3px solid ${PURPLE}`, backgroundColor: PURPLE_TINT, borderRadius: "0 6px 6px 0", padding: "0.4rem 0.6rem", color: "var(--ev-text)", margin: "0.4rem 0" }}>
                {inline(b.text, k)}
              </div>
            );
          case "ul":
            return (
              <ul key={k} style={{ margin: "0.3rem 0", paddingLeft: "1.1rem", listStyle: "disc" }}>
                {b.items.map((it, j) => <li key={j} style={{ marginBottom: "0.15rem" }}>{inline(it, `${k}-${j}`)}</li>)}
              </ul>
            );
          case "ol":
            return (
              <ol key={k} style={{ margin: "0.3rem 0", paddingLeft: "1.3rem" }}>
                {b.items.map((it, j) => <li key={j} style={{ marginBottom: "0.15rem" }}>{inline(it, `${k}-${j}`)}</li>)}
              </ol>
            );
          case "table":
            return (
              <div key={k} style={{ overflowX: "auto", margin: "0.5rem 0", border: `1px solid ${PURPLE_TINT_STRONG}`, borderRadius: "6px" }}>
                <table style={{ fontSize: "0.72rem", marginBottom: 0, width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {b.header.map((c, j) => (
                        <th key={j} style={{ whiteSpace: "nowrap", textAlign: "left", padding: "0.35rem 0.55rem", background: `linear-gradient(135deg, ${PURPLE} 0%, #5B3D86 100%)`, color: "#fff", fontWeight: 700 }}>
                          {inline(c, `${k}-h${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r} style={{ backgroundColor: r % 2 ? PURPLE_TINT : "transparent" }}>
                        {row.map((c, j) => (
                          <td key={j} style={{ whiteSpace: "nowrap", padding: "0.3rem 0.55rem", borderTop: `1px solid ${PURPLE_TINT_STRONG}` }}>
                            {inline(c, `${k}-${r}-${j}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return <p key={k} style={{ margin: "0.35rem 0" }}>{inline(b.text, k)}</p>;
        }
      })}
    </div>
  );
};

export default Markdown;
